import { useEffect, useRef, useState } from 'react'
import {
  PROJECT_BILLING_SCHEMES,
  PROJECT_TYPES,
  billingIntervalAllowed,
  billingIntervalsFor,
  erfuelltRecht,
  type ProjectStatus,
} from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { api } from '@/lib/axios'
import { bestaetige } from '@/lib/dialoge'
import { cardTitle, formCardSm } from '@/lib/form-styles'
import {
  Abschnitt,
  FeldStatus,
  FeldZeile,
  Feldgruppe,
  feldKlasse,
  mitStand,
  useAenderung,
  useEntwurf,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'
import { useTerminology } from '@/lib/useTerminology'
import {
  fetchPartner,
  fetchProjectHistory,
  fetchSites,
  fetchUsers,
  saveExtraAddresses,
  type PartnerAuswahl,
} from '../api/projects-head.api'
import {
  APPROVAL_KIND_LABELS,
  ausloeserText,
  BILLING_INTERVAL_LABELS,
  BILLING_SCHEME_LABELS,
  PROJECT_ORIGIN_LABELS,
  PROJECT_STATUS_LABELS,
  PROJECT_TYPE_LABELS,
  alsTag,
  alsZeitpunkt,
  statusText,
} from '../project-labels'
import type { ExtraAddress, LookupSite, LookupUser, ProjectApproval, ProjectDetail, ResolvedAddress } from '../types/project.types'
import { TechFieldsCard } from './TechFieldsCard'
import { AuftragsDokumente } from './AuftragsDokumente'
import { ProjectStatusDialog } from './ProjectStatusDialog'
import { AdressSuche, KundenSuche, RollenBenutzer, adressZeile, alsNutzlast, leereAdresse, type AdressEingabe } from './auswahl'

/**
 * AP-35 Detailblatt 1 5.8 — **Reiter Projektdaten.**
 *
 * Sechs Abschnitte in zwei Spalten, darunter Technik und Verlauf.
 * **Seit AP-36 Phase F ohne Stift, feldweise:** Die Felder stehen immer da und
 * speichern beim Verlassen, Auswahl und Kontrollkästchen sofort; geschrieben
 * wird über die eine Speicherstelle des Projekts (`.claude/rules/speichern.md`).
 *
 * Was der Status sperrt oder wozu das Recht fehlt, steht **gesperrt** da und
 * sieht aus wie Text (Blatt 1 3.3): storniert → nichts änderbar. Der Server
 * prüft es mit `PROJECT_READONLY` ein zweites Mal.
 *
 * Geleert heißt geleert: Ein leeres Feld geht als `null` an den Server, nicht
 * als „nicht angefasst".
 */

/** Storniert ist gesperrt (Blatt 1 3.3) — seit den vier Stufen der einzige gesperrte Status. */
const NUR_LESEN: ProjectStatus[] = ['CANCELLED']

/**
 * Unter diesem Schlüssel reist die **ganze Liste** der weiteren Adressen durch
 * die Speicherstelle (8.3 a). `schreibe` erkennt ihn und nimmt den eigenen
 * Endpunkt — so bleibt es eine Stelle je Datensatz.
 */
const WEITERE = 'extraAddresses'

type WeitereNutzlast = { label: string; addressId?: string | null; addressJson?: Record<string, unknown> | null }

export function ProjectDataTab({ projekt, onChanged }: { projekt: ProjectDetail; onChanged: () => void }) {
  const { user } = useAuthStore()
  const terms = useTerminology()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const [verlauf, setVerlauf] = useState<ProjectApproval[]>([])
  const [benutzer, setBenutzer] = useState<LookupUser[]>([])
  const [betriebsstaetten, setBetriebsstaetten] = useState<LookupSite[]>([])

  useEffect(() => {
    void fetchProjectHistory(projekt.id).then(setVerlauf).catch(() => undefined)
  }, [projekt.id, projekt.status, onChanged])

  // Die Auswahllisten werden immer geladen: Ein gesperrtes Feld zeigt seinen
  // Namen, kein leeres Dropdown (4.1).
  useEffect(() => {
    void fetchUsers().then(setBenutzer).catch(() => undefined)
    void fetchSites().then(setBetriebsstaetten).catch(() => undefined)
  }, [])

  // AP-36 4.2: Nach dem Anlegen geht es hier weiter — der Fokus springt einmalig
  // in das erste leere Feld, damit sich die Erfassung fortsetzt, wo der Dialog
  // aufgehört hat. Das Kennzeichen kommt aus der Adresse und wird danach entfernt,
  // sonst spränge der Fokus bei jedem Neuzeichnen.
  const bereich = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const adresse = new URL(window.location.href)
    if (adresse.searchParams.get('neu') !== '1') return
    adresse.searchParams.delete('neu')
    window.history.replaceState(null, '', adresse.toString())
    const felder = bereich.current?.querySelectorAll<HTMLInputElement>('input:not([disabled]):not([type=checkbox])')
    const erstes = [...(felder ?? [])].find((f) => !f.value)
    erstes?.focus()
  }, [])

  const darfAendern = darf('projects.project.manage')
  const preise = darf('projects.price.view')
  const status = projekt.status as ProjectStatus
  /** Darf dieses Feld hier bearbeitet werden? Sonst steht es gesperrt da. */
  const offen = (_feld: string) => darfAendern && !NUR_LESEN.includes(status)

  /**
   * **Eine Speicherstelle für das Projekt** (7.2). Jedes Feld schickt nur sich
   * selbst; danach wird neu geladen, weil Kopf und Nachbarreiter denselben
   * Datensatz zeigen. Die Rückfrage beim Kundenwechsel beantwortet der
   * Baustein (3.6), nicht die Karte.
   */
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `project:${projekt.id}`,
    bezeichnung: `${terms.project} ${projekt.projectNumber}`,
    daten: projekt as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      if (WEITERE in patch) {
        const neu = await saveExtraAddresses(projekt.id, (patch[WEITERE] ?? []) as WeitereNutzlast[])
        onChanged()
        return neu as unknown as Datensatz
      }
      const res = await api.patch<{ data: ProjectDetail }>(`/projects/${projekt.id}`, patch, mitStand(stand))
      onChanged()
      return res.data.data as unknown as Datensatz
    },
    keepalive: { url: `/projects/${projekt.id}`, method: 'PATCH' },
    rueckfragen: {
      PROJECT_CLIENT_CHANGE_WARN: {
        frage: (koerper) =>
          `${(koerper.counts as { postedDocuments?: number } | undefined)?.postedDocuments ?? 0} festgeschriebene Belege tragen noch den alten Kunden. Sie bleiben unverändert. Kunde trotzdem ändern?`,
        kennzeichen: { confirmClientChange: true },
        ok: 'Kunde ändern',
      },
    },
  }) as Speicherstelle<unknown>

  const gemeinsam = { projekt, stelle, offen }
  return (
    <div className="grid gap-4 lg:grid-cols-2" ref={bereich}>
      <StammKarte {...gemeinsam} terms={terms} benutzer={benutzer} betriebsstaetten={betriebsstaetten} darfStatus={darfAendern} onChanged={onChanged} />
      <TermineKarte {...gemeinsam} />
      <KundeKarte {...gemeinsam} />
      <KaufmaennischKarte {...gemeinsam} benutzer={benutzer} preise={preise} />
      <AdressenKarte {...gemeinsam} />
      <AnmerkungenKarte {...gemeinsam} />

      {/* Blatt 6 2.6: die Technik-Vorgabe gilt für alle Positionen, die nicht abweichen. */}
      <div className="lg:col-span-2">
        <TechFieldsCard projectId={projekt.id} darfAendern={darfAendern} ebene="projekt" />
      </div>

      <div className={`${formCardSm} lg:col-span-2`}>
        <h3 className={cardTitle}>Verlauf</h3>
        {verlauf.length === 0 && <p className="mt-2 text-sm text-muted-foreground">Noch keine Einträge.</p>}
        <ol className="mt-3 space-y-2">
          {verlauf.map((e) => (
            <li key={e.id} className="flex flex-wrap items-baseline gap-2 text-sm">
              <span className="w-36 shrink-0 text-xs text-muted-foreground">{alsZeitpunkt(e.approvedAt)}</span>
              <span className="font-medium">{APPROVAL_KIND_LABELS[e.kind] ?? e.kind}</span>
              {e.fromStatus && e.toStatus && e.kind === 'STATUS_CHANGE' && (
                <span>
                  {statusText(e.fromStatus)} → {statusText(e.toStatus)}
                </span>
              )}
              <span className="text-muted-foreground">{e.userName ?? (e.trigger ? `System (${ausloeserText(e.trigger)})` : 'System')}</span>
              {e.note && <span className="text-muted-foreground">· {e.note}</span>}
              {e.revokedAt && <span className="text-destructive">· widerrufen {e.revokeReason ? `(${e.revokeReason})` : ''}</span>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}

interface KartenProps {
  projekt: ProjectDetail
  stelle: Speicherstelle<unknown>
  offen: (feld: string) => boolean
}

// ── Projektdaten ───────────────────────────────────────────────────────────

function StammKarte({
  projekt,
  stelle,
  offen,
  terms,
  benutzer,
  betriebsstaetten,
  darfStatus,
  onChanged,
}: KartenProps & {
  terms: ReturnType<typeof useTerminology>
  benutzer: LookupUser[]
  betriebsstaetten: LookupSite[]
  darfStatus: boolean
  onChanged: () => void
}) {
  const name = useFeld(stelle, 'name', { label: 'Bezeichnung', pflicht: true, gesperrt: !offen('name') })
  const typ = useFeld(stelle, 'type', { art: 'auswahl', label: 'Typ', gesperrt: !offen('type') })
  const waehrung = useFeld(stelle, 'currency', {
    label: 'Währung',
    pflicht: true,
    gesperrt: !offen('currency'),
    lies: (t) => (t.trim() === '' ? null : t.trim().toUpperCase()),
  })
  const betriebsstaette = useFeld<string | null>(stelle, 'siteId', { art: 'auswahl', label: 'Betriebsstätte', gesperrt: !offen('siteId') })
  const leitung = useFeld<string | null>(stelle, 'projectLeadUserId', { art: 'auswahl', label: terms.lead, gesperrt: !offen('projectLeadUserId') })

  return (
    <Abschnitt titel={terms.data}>
      <FeldZeile label="Bezeichnung" status={name}>
        <input className={`${feldKlasse} font-semibold`} {...name.props} />
      </FeldZeile>
      <FeldZeile label="Typ" status={typ}>
        <select className={feldKlasse} {...typ.props}>
          {PROJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {PROJECT_TYPE_LABELS[t]?.(terms) ?? t}
            </option>
          ))}
        </select>
      </FeldZeile>
      <FeldZeile label="Status">
        <StatusWahl projekt={projekt} darf={darfStatus} onChanged={onChanged} />
      </FeldZeile>
      <FeldZeile label="Währung" status={waehrung}>
        <input className={`${feldKlasse} w-24`} maxLength={3} {...waehrung.props} />
      </FeldZeile>
      <FeldZeile label="Betriebsstätte" status={betriebsstaette}>
        <select className={feldKlasse} {...betriebsstaette.props}>
          <option value="">— keine —</option>
          {betriebsstaetten.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </FeldZeile>
      <FeldZeile label={terms.lead} status={leitung}>
        {/* `RollenBenutzer` reicht keine `disabled`-Eigenschaft durch — das Feldset sperrt sein Auswahlfeld. */}
        <fieldset disabled={!offen('projectLeadUserId')} className="contents">
          <RollenBenutzer
            benutzer={benutzer}
            rolle="LEAD"
            className={feldKlasse}
            wert={leitung.wert ?? ''}
            onChange={(v) => leitung.setze(v || null)}
          />
        </fieldset>
      </FeldZeile>
      <FeldZeile label="Herkunft">{PROJECT_ORIGIN_LABELS[projekt.origin] ?? projekt.origin}</FeldZeile>
      {projekt.legacyNumber && <FeldZeile label="Fremdnummer">{projekt.legacyNumber}</FeldZeile>}
      <FeldZeile label="angelegt am">{alsTag(projekt.createdAt)}</FeldZeile>
    </Abschnitt>
  )
}

// ── Termine ────────────────────────────────────────────────────────────────

function TermineKarte({ stelle, offen }: KartenProps) {
  const bis = useFeld(stelle, 'completionDueAt', { art: 'datum', label: 'Fertigstellung bis', gesperrt: !offen('completionDueAt') })
  const zusatz = useFeld(stelle, 'completionDueNote', { label: 'Zusatz zum Termin', gesperrt: !offen('completionDueNote') })
  const gewaehrleistung = useFeld(stelle, 'warrantyEndsAt', { art: 'datum', label: 'Gewährleistung bis', gesperrt: !offen('warrantyEndsAt') })

  return (
    <Abschnitt titel="Termine">
      <FeldZeile label="Fertigstellung bis" status={bis}>
        <input type="date" className={feldKlasse} {...bis.props} />
      </FeldZeile>
      <FeldZeile label="Zusatz zum Termin" status={zusatz}>
        <input className={feldKlasse} placeholder="KW 42, nach Freigabe …" {...zusatz.props} />
      </FeldZeile>
      <FertigFeld stelle={stelle} gesperrt={!offen('completedAt')} />
      <FeldZeile label="Gewährleistung bis" status={gewaehrleistung}>
        <input type="date" className={feldKlasse} {...gewaehrleistung.props} />
      </FeldZeile>
    </Abschnitt>
  )
}

const TAG = /^\d{4}-\d{2}-\d{2}$/

/**
 * AP-36 Abschnitt 9 — **„fertiggestellt am" fragt vor dem Senden.** Ein
 * gesetztes Datum schließt das Projekt ab, ein entferntes setzt es wieder auf
 * aktiv. Deshalb kein gewöhnliches Feld, sondern eine Handlung mit eigener
 * Eingabe: Bei „Abbrechen" bleibt der gespeicherte Wert stehen, und hinaus geht
 * der Wert nur nach einem Ja.
 */
function FertigFeld({ stelle, gesperrt }: { stelle: Speicherstelle<unknown>; gesperrt: boolean }) {
  const daten = useStellenDaten(stelle)
  const gespeichert = typeof daten.completedAt === 'string' ? daten.completedAt.slice(0, 10) : ''
  const [eingabe, setEingabe] = useState<string | null>(null)
  const aenderung = useAenderung(stelle, 'completedAt')

  async function verlassen() {
    if (eingabe === null || gesperrt) return
    const neu = eingabe
    if (neu === gespeichert) {
      setEingabe(null)
      return
    }
    // Ein unvollständiges Datum geht nicht hinaus (3.4) — die Eingabe bleibt stehen.
    if (neu !== '' && !TAG.test(neu)) return
    const ok = await bestaetige(neu ? 'Das Projekt wird damit abgeschlossen.' : 'Das Projekt wird damit wieder aktiv.')
    if (!ok) {
      setEingabe(null)
      return
    }
    const ergebnis = await aenderung.sende({
      felder: ['completedAt'],
      labels: { completedAt: 'fertiggestellt am' },
      patch: { completedAt: neu || null },
      wert: 'completedAt',
    })
    if (ergebnis.art !== 'fehler') setEingabe(null)
  }

  return (
    <FeldZeile label="fertiggestellt am" status={aenderung.status} hilfe="Ein Datum stellt fertig, ein entferntes setzt wieder auf „Aktiv“.">
      <input
        type="date"
        className={feldKlasse}
        disabled={gesperrt || undefined}
        value={eingabe ?? gespeichert}
        onChange={(e) => setEingabe(e.target.value)}
        onBlur={() => void verlassen()}
      />
    </FeldZeile>
  )
}

// ── Kundenauftrag ──────────────────────────────────────────────────────────

function KundeKarte({ stelle, offen }: KartenProps) {
  const daten = useStellenDaten(stelle)
  const kunde = useFeld<string | null>(stelle, 'clientId', {
    art: 'auswahl',
    label: 'Kunde',
    gesperrt: !offen('clientId'),
    // Ein neuer Kunde nimmt den alten Ansprechpartner nicht mit — der gehört zum alten.
    folge: () => ({ contactPersonId: null }),
  })
  const ansprechpartner = useFeld<string | null>(stelle, 'contactPersonId', {
    art: 'auswahl',
    label: 'Ansprechpartner',
    gesperrt: !offen('contactPersonId'),
  })
  const bezeichnung = useFeld(stelle, 'customerOrderRef', { label: 'Bezeichnung', gesperrt: !offen('customerOrderRef') })
  const nummer = useFeld(stelle, 'customerOrderNumber', { label: 'Auftragsnummer', gesperrt: !offen('customerOrderNumber') })
  const position = useFeld(stelle, 'customerOrderPosition', { label: 'Position', gesperrt: !offen('customerOrderPosition') })

  const [partner, setPartner] = useState<PartnerAuswahl | null>(null)
  /** Der eben gewählte Kunde — sein Name steht im Feld, bevor die Antwort da ist. */
  const [gewaehlt, setGewaehlt] = useState<{ id: string; name: string } | null>(null)
  const kundeId = kunde.wert ?? ''

  // Bei jedem Kundenwechsel neu — ein Ansprechpartner kann im CRM inzwischen dazugekommen sein.
  useEffect(() => {
    if (!kundeId) {
      setPartner(null)
      return
    }
    void fetchPartner(kundeId)
      .then(setPartner)
      .catch(() => setPartner(null))
  }, [kundeId])

  const gespeicherterName = (daten.client as { name?: string } | null | undefined)?.name ?? ''
  const anzeige = kundeId ? { id: kundeId, name: gewaehlt?.id === kundeId ? gewaehlt.name : gespeicherterName } : null
  const gespeicherterPartner = daten.contactPerson as { id: string; firstName: string; lastName?: string | null } | null | undefined
  const bekannt = (partner?.contacts ?? []).some((c) => c.id === ansprechpartner.wert)

  return (
    <Abschnitt titel="Kundenauftrag">
      <FeldZeile label="Kunde" status={kunde}>
        <KundenSuche
          wert={anzeige}
          deaktiviert={!offen('clientId')}
          onWahl={(k) => {
            setGewaehlt(k)
            kunde.setze(k?.id ?? null)
          }}
        />
      </FeldZeile>
      <FeldZeile label="Ansprechpartner" status={ansprechpartner}>
        <select className={feldKlasse} {...ansprechpartner.props} disabled={ansprechpartner.props.disabled || !kundeId || undefined}>
          <option value="">— keiner —</option>
          {/* Solange die Liste des Partners nicht da ist, bleibt der gespeicherte Ansprechpartner sichtbar. */}
          {ansprechpartner.wert && !bekannt && gespeicherterPartner?.id === ansprechpartner.wert && (
            <option value={gespeicherterPartner.id}>{`${gespeicherterPartner.firstName} ${gespeicherterPartner.lastName ?? ''}`.trim()}</option>
          )}
          {(partner?.contacts ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {`${c.firstName} ${c.lastName ?? ''}`.trim()}
              {c.position ? ` · ${c.position}` : ''}
            </option>
          ))}
        </select>
      </FeldZeile>
      <FeldZeile label="Bezeichnung" status={bezeichnung}>
        <input className={feldKlasse} {...bezeichnung.props} />
      </FeldZeile>
      <FeldZeile label="Auftragsnummer" status={nummer}>
        <input className={feldKlasse} {...nummer.props} />
      </FeldZeile>
      <FeldZeile label="Position" status={position}>
        <input className={feldKlasse} {...position.props} />
      </FeldZeile>
    </Abschnitt>
  )
}

// ── Kaufmännisches ─────────────────────────────────────────────────────────

function KaufmaennischKarte({ projekt, stelle, offen, benutzer, preise }: KartenProps & { benutzer: LookupUser[]; preise: boolean }) {
  const budget = useFeld<number | null>(stelle, 'budget', { art: 'zahl', label: 'Grobbudget', gesperrt: !offen('budget') })
  const prozent = useFeld<number | null>(stelle, 'retentionPercent', {
    art: 'zahl',
    label: 'Haftrücklass in Prozent',
    gesperrt: !offen('retentionPercent'),
    pruefe: (w) => (w !== null && (w < 0 || w > 100) ? 'Zwischen 0 und 100' : null),
  })
  const betrag = useFeld<number | null>(stelle, 'retentionAmount', { art: 'zahl', label: 'Haftrücklass als Betrag', gesperrt: !offen('retentionAmount') })
  const verkauf = useFeld<string | null>(stelle, 'salesUserId', { art: 'auswahl', label: 'Verkauf', gesperrt: !offen('salesUserId') })
  const kalkulation = useFeld<string | null>(stelle, 'calculationUserId', { art: 'auswahl', label: 'Kalkulation', gesperrt: !offen('calculationUserId') })

  return (
    // Nachtrag 8: eigener Bereich unter den Feldern mit eigener Bedienung.
    <Abschnitt titel="Kaufmännisches" unten={<AuftragsDokumente projectId={projekt.id} />}>
      {/* Schema und Intervall prüft der Server zusammen (Nachtrag 2) — also eine Gruppe. */}
      <Feldgruppe
        stelle={stelle}
        label="Abrechnung"
        pruefe={(werte) => {
          const schema = werte.billingScheme ? String(werte.billingScheme) : ''
          const intervall = werte.billingInterval ? String(werte.billingInterval) : ''
          if (!schema || !intervall) return null
          return billingIntervalAllowed(schema, intervall) ? null : 'Das Intervall passt nicht zum Abrechnungsschema.'
        }}
      >
        <AbrechnungsFelder stelle={stelle} gesperrt={!offen('billingScheme')} />
      </Feldgruppe>

      {/* Ohne Preisrecht sind diese Felder nicht leer, sondern **weg** — der Server schickt sie nicht (Blatt 1 6.). */}
      {preise && (
        <FeldZeile label="Grobbudget" status={budget}>
          <input className={`${feldKlasse} w-40 text-right`} inputMode="decimal" {...budget.props} />
        </FeldZeile>
      )}
      {preise && (
        <FeldZeile label="Haftrücklass" status={prozent}>
          <span className="flex items-center gap-2">
            <input className={`${feldKlasse} w-20 text-right`} inputMode="decimal" {...prozent.props} />
            <span className="text-muted-foreground">% oder Betrag</span>
            <input className={`${feldKlasse} w-32 text-right`} inputMode="decimal" {...betrag.props} />
          </span>
          <FeldStatus status={betrag} zeichen={false} />
        </FeldZeile>
      )}
      <FeldZeile label="Verkauf" status={verkauf}>
        <fieldset disabled={!offen('salesUserId')} className="contents">
          <RollenBenutzer benutzer={benutzer} rolle="SALES" className={feldKlasse} wert={verkauf.wert ?? ''} onChange={(v) => verkauf.setze(v || null)} />
        </fieldset>
      </FeldZeile>
      <FeldZeile label="Kalkulation" status={kalkulation}>
        <fieldset disabled={!offen('calculationUserId')} className="contents">
          <RollenBenutzer
            benutzer={benutzer}
            rolle="CALCULATION"
            className={feldKlasse}
            wert={kalkulation.wert ?? ''}
            onChange={(v) => kalkulation.setze(v || null)}
          />
        </fieldset>
      </FeldZeile>
    </Abschnitt>
  )
}

/** Die beiden Felder der Gruppe — der Hook läuft im Kontext der `Feldgruppe`. */
function AbrechnungsFelder({ stelle, gesperrt }: { stelle: Speicherstelle<unknown>; gesperrt: boolean }) {
  /** Das Intervall zum Zeitpunkt des Sendens — das Folgefeld liest es aus dem Verweis. */
  const intervallJetzt = useRef<string | null>(null)
  const schema = useFeld<string | null>(stelle, 'billingScheme', {
    art: 'auswahl',
    label: 'Abrechnungsschema',
    gesperrt,
    // Passt das Intervall nicht zum neuen Schema, geht das erste erlaubte als Folgefeld mit.
    folge: (w) => (billingIntervalAllowed(w, intervallJetzt.current) ? {} : { billingInterval: billingIntervalsFor(w)[0] ?? null }),
  })
  const intervall = useFeld<string | null>(stelle, 'billingInterval', { art: 'auswahl', label: 'Abrechnungsintervall', gesperrt })
  intervallJetzt.current = intervall.wert
  const erlaubt = billingIntervalsFor(schema.wert)
  const fremd = intervall.wert && !(erlaubt as readonly string[]).includes(intervall.wert)

  return (
    <FeldZeile label="Abrechnung" status={schema}>
      <span className="flex flex-wrap gap-2">
        <select className={`${feldKlasse} w-auto`} {...schema.props}>
          {!schema.wert && <option value="">—</option>}
          {PROJECT_BILLING_SCHEMES.map((sch) => (
            <option key={sch} value={sch}>
              {BILLING_SCHEME_LABELS[sch] ?? sch}
            </option>
          ))}
        </select>
        <select className={`${feldKlasse} w-auto`} {...intervall.props} disabled={intervall.props.disabled || !schema.wert || undefined}>
          {/* Ein gespeichertes Intervall, das nicht mehr zum Schema passt, bleibt sichtbar. */}
          {(!intervall.wert || fremd) && (
            <option value={intervall.wert ?? ''}>{(intervall.wert && BILLING_INTERVAL_LABELS[intervall.wert]) || '—'}</option>
          )}
          {erlaubt.map((i) => (
            <option key={i} value={i}>
              {BILLING_INTERVAL_LABELS[i] ?? i}
            </option>
          ))}
        </select>
      </span>
    </FeldZeile>
  )
}

// ── Adressen (Blatt 1 2.4: Verweis oder freie Anschrift, nie beides) ────────

type AdressArt = 'orderer' | 'billing' | 'site'
const ADRESS_LABEL: Record<AdressArt, string> = { orderer: 'Auftraggeber', billing: 'Rechnung', site: 'Baustelle' }
const ARTEN: AdressArt[] = ['orderer', 'billing', 'site']

function ausAdresse(a: ResolvedAddress): AdressEingabe {
  const o = (a.own ?? {}) as Record<string, string>
  return {
    ...leereAdresse(),
    modus: a.source,
    addressId: a.addressId ?? '',
    anzeige: a.source === 'partner' && a.effective ? adressZeile(a.effective as never) : '',
    recipient: o.recipient ?? '',
    street: o.street ?? '',
    addition: o.addition ?? '',
    zip: o.zip ?? '',
    city: o.city ?? '',
    country: o.country ?? 'AT',
  }
}

/** Anzeige einer Adresse: Firma zuerst (Nachtrag 2), dann Anschrift, dazu die Herkunft. */
function AdressAnzeige({ a }: { a: ResolvedAddress }) {
  if (!a.effective) return <>—</>
  return (
    <>
      {adressZeile(a.effective as never)}{' '}
      <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{a.source === 'partner' ? 'Partner' : 'eigen'}</span>
    </>
  )
}

function AdressenKarte({ projekt, stelle, offen }: KartenProps) {
  const daten = useStellenDaten(stelle)
  const adressen = (daten.addresses as ProjectDetail['addresses'] | undefined) ?? projekt.addresses

  return (
    <Abschnitt titel="Adressen">
      {ARTEN.map((art) => (
        <FesteAdresse key={art} stelle={stelle} art={art} adresse={adressen[art]} gesperrt={!offen(`${art}AddressId`)} />
      ))}
      <WeitereAdressen stelle={stelle} eintraege={adressen.extra ?? []} gesperrt={!offen(WEITERE)} />
    </Abschnitt>
  )
}

/**
 * Eine der drei festen Adressen. Verweis und freie Anschrift gehören zusammen
 * (nie beides), also ist der Eintrag **ein** Entwurf und geht beim Verlassen in
 * einem PATCH hinaus (8.3 a).
 */
function FesteAdresse({
  stelle,
  art,
  adresse,
  gesperrt,
}: {
  stelle: Speicherstelle<unknown>
  art: AdressArt
  adresse: ResolvedAddress
  gesperrt: boolean
}) {
  const eintrag = useEntwurf<AdressEingabe>(stelle, {
    kennung: `${art}Address`,
    gespeichert: ausAdresse(adresse),
    felder: [`${art}AddressId`, `${art}AddressJson`],
    label: ADRESS_LABEL[art],
    gesperrt,
    operation: (entwurf) => () => {
      const n = alsNutzlast(entwurf)
      return { [`${art}AddressId`]: n.addressId, [`${art}AddressJson`]: n.addressJson }
    },
  })
  const wert = eintrag.entwurf ?? ausAdresse(adresse)

  /**
   * Eine gewählte Partneradresse und „leeren" sind eine Auswahl und speichern
   * sofort (3.2); die freie Anschrift speichert beim Verlassen des Eintrags.
   * Gesendet wird im Effekt, nicht im Rückruf: `speichere` liest den Entwurf,
   * den der letzte Render gesehen hat — unmittelbar nach `setze` wäre das noch
   * der alte.
   */
  const sofort = useRef(false)
  useEffect(() => {
    if (!sofort.current) return
    sofort.current = false
    void eintrag.speichere()
  })

  return (
    <div className="space-y-1 border-b border-border/60 pb-2" {...eintrag.huelle}>
      <p className="text-sm text-muted-foreground">{ADRESS_LABEL[art]}</p>
      {gesperrt ? (
        <p className="text-sm text-foreground">
          <AdressAnzeige a={adresse} />
        </p>
      ) : (
        <AdressSuche
          wert={wert}
          onChange={(a) => {
            eintrag.setze(a)
            if ((a.modus === 'partner' && a.addressId !== wert.addressId) || (a.modus === 'none' && wert.modus !== 'none')) sofort.current = true
          }}
        />
      )}
      <FeldStatus status={eintrag} />
    </div>
  )
}

type WeitereEingabe = { label: string; adresse: AdressEingabe }

/**
 * Die weiteren Adressen sind eine **Liste als Ganzes** (8.3 a): Sie gehen über
 * ihren eigenen Endpunkt hinaus, aber durch dieselbe Speicherstelle — beim
 * Verlassen des Blocks, nicht auf Knopfdruck.
 */
function WeitereAdressen({ stelle, eintraege, gesperrt }: { stelle: Speicherstelle<unknown>; eintraege: ExtraAddress[]; gesperrt: boolean }) {
  const liste = useEntwurf<WeitereEingabe[]>(stelle, {
    kennung: WEITERE,
    gespeichert: eintraege.map((x) => ({ label: x.label, adresse: ausAdresse(x) })),
    felder: [WEITERE],
    label: 'weitere Adressen',
    gesperrt,
    operation: (entwurf) => () => ({
      [WEITERE]: entwurf.filter((w) => w.label.trim()).map((w) => ({ label: w.label.trim(), ...alsNutzlast(w.adresse) })),
    }),
  })
  const werte = liste.entwurf ?? []

  if (gesperrt) {
    return (
      <>
        {eintraege.map((x) => (
          <p key={x.id} className="text-sm text-foreground">
            <span className="text-muted-foreground">{x.label}: </span>
            <AdressAnzeige a={x} />
          </p>
        ))}
      </>
    )
  }

  return (
    <div className="space-y-1" {...liste.huelle}>
      {werte.map((w, i) => (
        <div key={i} className="space-y-1 border-b border-border/60 pb-2">
          <div className="flex items-center gap-2">
            <input
              className={`${feldKlasse} w-56`}
              placeholder="Bezeichnung (z. B. Lieferung Werk 2)"
              value={w.label}
              onChange={(e) => liste.setze((alt) => alt.map((y, j) => (j === i ? { ...y, label: e.target.value } : y)))}
            />
            <button
              type="button"
              className="text-xs text-destructive underline"
              onClick={() => liste.setze((alt) => alt.filter((_, j) => j !== i))}
            >
              entfernen
            </button>
          </div>
          <AdressSuche wert={w.adresse} onChange={(a) => liste.setze((alt) => alt.map((y, j) => (j === i ? { ...y, adresse: a } : y)))} />
        </div>
      ))}
      <button
        type="button"
        className="text-sm text-primary underline"
        onClick={() => liste.setze((alt) => [...alt, { label: '', adresse: leereAdresse() }])}
      >
        + weitere Adresse
      </button>
      <FeldStatus status={liste} />
    </div>
  )
}

// ── Anmerkungen ────────────────────────────────────────────────────────────

function AnmerkungenKarte({ stelle, offen }: KartenProps) {
  const anmerkungen = useFeld(stelle, 'notes', { label: 'Anmerkungen', gesperrt: !offen('notes') })
  const beschreibung = useFeld(stelle, 'description', { label: 'Beschreibung', gesperrt: !offen('description') })

  return (
    <Abschnitt titel="Anmerkungen">
      <FeldZeile label="Anmerkungen" status={anmerkungen}>
        <textarea className={feldKlasse} rows={4} {...anmerkungen.props} />
      </FeldZeile>
      <FeldZeile label="Beschreibung" status={beschreibung}>
        <textarea className={feldKlasse} rows={3} {...beschreibung.props} />
      </FeldZeile>
    </Abschnitt>
  )
}

/**
 * AP-35 Nachtrag 5 (N5-12) — **der Status wechselt nur hier**, nicht mehr im Kopf.
 *
 * Angeboten werden nur die Ziele, die der Server erlaubt (`allowedTransitions`);
 * die Wahl öffnet den Statusdialog mit Begründung bzw. Fertigstellungsterminen.
 * Ein Statuswechsel ist eine Handlung, kein Feld — deshalb gilt er sofort.
 */
function StatusWahl({ projekt, darf, onChanged }: { projekt: ProjectDetail; darf: boolean; onChanged: () => void }) {
  const [ziel, setZiel] = useState<ProjectStatus | null>(null)
  const anzeige = PROJECT_STATUS_LABELS[projekt.status as ProjectStatus]
  const ziele = projekt.allowedTransitions as ProjectStatus[]
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${anzeige?.class ?? ''}`}>{anzeige?.label ?? projekt.status}</span>
      {darf && ziele.length > 0 && (
        <select
          className="rounded-md border border-input bg-background px-2 py-0.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          value=""
          aria-label="Status ändern"
          onChange={(e) => e.target.value && setZiel(e.target.value as ProjectStatus)}
        >
          <option value="">Ändern zu …</option>
          {ziele.map((z) => (
            <option key={z} value={z}>
              {PROJECT_STATUS_LABELS[z]?.label ?? z}
            </option>
          ))}
        </select>
      )}
      {darf && ziele.length === 0 && (
        <span className="text-xs text-muted-foreground">
          Kein Wechsel möglich{projekt.type === 'COLLECTIVE' ? ' — der Status folgt den Positionen' : ''}
        </span>
      )}
      {ziel && (
        <ProjectStatusDialog
          projekt={projekt}
          ziel={ziel}
          onClose={() => setZiel(null)}
          onDone={() => {
            setZiel(null)
            onChanged()
          }}
        />
      )}
    </span>
  )
}
